/**
 * readAloudCopy — the words on the readout player, and the one line it leaves
 * behind when a readout drops out mid-sentence.
 *
 * D9 (granted exception, 2026-08-24): code may author FAILURE copy and chrome —
 * never content. The player's buttons are chrome; the dropped-out line is a
 * failure notice, deliberately deterministic because when it fires the voice
 * lane is the broken part. It matches the server's own inventory
 * (functions/src/prest0nSession/voiceCopy.ts) in voice and shape: lowercase,
 * warm, no jargon, no vendor, no error code, and the reader's own language.
 *
 * Pure and IO-free apart from the language lookup.
 */

import { getUserUiLanguage } from '../threadMetadata/uiLanguage';

export type ReadAloudLang = 'en' | 'es';

export interface ReadAloudPlayerCopy {
  /** Parked with the section controls (2026-09-10) — kept so the copy table
   *  does not have to be rebuilt when segment navigation is reconsidered. */
  previous: string;
  next: string;
  pause: string;
  play: string;
  restart: string;
  slower: string;
  faster: string;
  speed: string;
  /** "2 / 5" — parked with the section controls; nothing renders it today. */
  position: (segment: number, total: number) => string;
  /** "1.2x" — the speed, in the units a listener already knows from podcasts. */
  rateLabel: (rate: number) => string;
  /** "120 %" — the same number, spelled out for the control's accessible name. */
  ratePercent: (rate: number) => string;
  /** What the reader is told while the voice is being made, before any sound. */
  preparing: string;
  /** The same, at a seam: the voice has run out of ready audio mid-readout. */
  preparingNext: string;
  /** "0:12" — how long the voice has been talking. */
  clock: (seconds: number) => string;
  /** The accessible name of the progress bar. */
  progress: string;
  player: string;
}

/** m:ss, floored — a readout is minutes at most, so no hours case. */
function clockOf(seconds: number): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

/** "1x", "1.2x" — never "1.0x", which reads like a machine wrote it. */
function rateLabelOf(rate: number): string {
  const rounded = Math.round(rate * 10) / 10;
  return `${Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)}x`;
}

const EN: ReadAloudPlayerCopy = {
  previous: 'previous section',
  next: 'next section',
  pause: 'pause',
  play: 'play',
  restart: 'start over',
  slower: 'slower',
  faster: 'faster',
  speed: 'speed',
  position: (segment, total) => `${segment} / ${total}`,
  rateLabel: rateLabelOf,
  ratePercent: (rate) => `${Math.round(rate * 100)} %`,
  preparing: 'getting the voice ready…',
  preparingNext: 'getting the next part ready…',
  clock: clockOf,
  progress: 'how far along the reading is',
  player: 'reading controls',
};

const ES: ReadAloudPlayerCopy = {
  previous: 'sección anterior',
  next: 'sección siguiente',
  pause: 'pausar',
  play: 'reproducir',
  restart: 'empezar de nuevo',
  slower: 'más lento',
  faster: 'más rápido',
  speed: 'velocidad',
  position: (segment, total) => `${segment} / ${total}`,
  rateLabel: rateLabelOf,
  ratePercent: (rate) => `${Math.round(rate * 100)} %`,
  preparing: 'preparando la voz…',
  preparingNext: 'preparando la siguiente parte…',
  clock: clockOf,
  progress: 'progreso de la lectura',
  player: 'controles de lectura',
};

export function readAloudLang(language?: string | null): ReadAloudLang {
  const raw = String(language || '').trim().toLowerCase();
  if (raw.startsWith('es')) return 'es';
  if (raw.startsWith('en')) return 'en';
  return getUserUiLanguage() === 'es' ? 'es' : 'en';
}

export function readAloudPlayerCopy(language?: string | null): ReadAloudPlayerCopy {
  return readAloudLang(language) === 'es' ? ES : EN;
}

/**
 * The readout was going and then it was not — a segment's audio failed twice,
 * a signed url went stale, a socket dropped. Says what happened and that trying
 * again is worth it, which is true: the render is cached, so the second attempt
 * is usually instant and free.
 */
export function readAloudPlaybackFailedNotice(language?: string | null): string {
  return readAloudLang(language) === 'es'
    ? 'la voz se cortó a mitad. inténtalo otra vez en un momento.'
    : 'the voice dropped out partway through. try again in a moment.';
}
